import _ from "lodash";
import { lazyCategories } from "../hooks/useCategories";
import { getPlayerByPassword, queryVideos, listGroups, queryLinks } from "./firebase";
import type { IGroup } from "../entities/group.entity";
import type { ISubCategory } from "../entities/category.entity";
import type { IVideo } from "../entities/video.entity";
import { LOOP_CATEGORIES_NAME } from "./constants";
import { ILink } from "../entities/link.entity";
import { hash } from "ohash";

const logger = {
	debug: (...args: any[]) => {
		// if (process.env.NODE_ENV === "development") {
		console.log(...args);
		// }
	},
};

export async function fetchAllContent(playerPassword: string) {
	if (!playerPassword) {
		throw new Error("Player password is required");
	}

	const player = await getPlayerByPassword(playerPassword);
	logger.debug("player", player);
	const videos = await queryVideos(player.groups);
	logger.debug("videos", videos);
	const groups = await listGroups();
	logger.debug("groups", groups);
	const links = await queryLinks(player.groups);
	logger.debug("links", links);
	// The player refetches because meta/contentVersion changed, so it must never
	// reuse a cached category snapshot: a folder moved into a newly created
	// subcategory would otherwise stay invisible until the TTL expired.
	const { categories } = await lazyCategories(true);
	logger.debug("categories", categories);

	const isVideoInGroup = (video: IVideo, group: IGroup) => {
		const includeName = video.groups.includes(group.name);
		const includeId = video.groups.includes(group.id);

		if (includeName) {
			return true;
		}

		if (includeId) {
			return true;
		}

		return false;
	};

	const sortVideosByEpisodeNumber = (videos: IVideo[]) => {
		return _.orderBy(videos, [
			(video) => video.episode_number ? 0 : 1, // Videos with episode_number first
			'episode_number' // Then sort by episode_number
		], ['asc', 'asc']);
	};

	const splitParts = (name: string) => {
		return name
			.split("/")
			.map((part) => part.trim())
			.filter(Boolean);
	};

	const normalizeName = (value: string) => {
		return value
			.normalize("NFKD")
			.replace(/[\u0300-\u036f]/g, "")
			.replace(/[^a-zA-Z0-9]+/g, "")
			.toLowerCase();
	};

	const normalizedPathKey = (categoryName: string, parts: string[]) => {
		return [categoryName, ...parts]
			.map(normalizeName)
			.filter(Boolean)
			.join("/");
	};

	const buildSubCategoryTree = (
		categoryName: string,
		subCategories: ISubCategory[],
	) => {
		const pathToHash = new Map<string, string>();
		const legacyToHash = new Map<string, string>();

		const mergeVideos = (target: ISubCategory, incoming: ISubCategory) => {
			if (!incoming.videos?.length) return;
			const merged = _.uniqBy(
				[...(target.videos || []), ...incoming.videos],
				"id",
			);
			target.videos = merged;
		};

		const reducedSubCategories: ISubCategory[] = [];

		for (const subCategory of subCategories) {
			const parts = splitParts(subCategory.name);
			if (parts.length === 0) continue;

			let currentList = reducedSubCategories;
			const accumulatedParts: string[] = [];

			for (let index = 0; index < parts.length; index++) {
				const isLeaf = index === parts.length - 1;
				accumulatedParts.push(parts[index]);
				const pathKey = normalizedPathKey(categoryName, accumulatedParts);
				const id = `sc_${hash(pathKey)}`;

				pathToHash.set(pathKey, id);
				if (isLeaf && subCategory.id) {
					legacyToHash.set(subCategory.id, id);
				}

				const node: ISubCategory = isLeaf
					? {
							...subCategory,
							id,
							name: parts[index],
							subCategories: subCategory.subCategories || [],
							videos: subCategory.videos || [],
					  }
					: {
							id,
							name: parts[index],
							subCategories: [],
							videos: [],
					  };

				const existing = currentList.find((item) => item.id === node.id);
				if (existing) {
					if (!existing.subCategories) {
						existing.subCategories = [];
					}
					mergeVideos(existing, node);
					currentList = existing.subCategories;
					continue;
				}

				currentList.push(node);
				currentList = node.subCategories || [];
			}
		}

		return { tree: reducedSubCategories, pathToHash, legacyToHash };
	};

	const normalizeFullPathKey = (path: string) => {
		const parts = path
			.replace(/\s*->\s*/g, "/")
			.split("/")
			.map((part) => part.trim())
			.filter(Boolean);
		if (parts.length === 0) return null;
		const [categoryName, ...subParts] = parts;
		if (!categoryName || subParts.length === 0) return null;
		return normalizedPathKey(categoryName, subParts);
	};

	const globalPathToHash = new Map<string, string>();
	const globalLegacyToHash = new Map<string, string>();

	const categoriesWithHashedSubCategories = categories.map((category) => {
		const { tree, pathToHash, legacyToHash } = buildSubCategoryTree(
			category.name,
			category.subCategories,
		);
		for (const [path, id] of pathToHash.entries()) {
			globalPathToHash.set(path, id);
		}
		for (const [legacy, id] of legacyToHash.entries()) {
			globalLegacyToHash.set(legacy, id);
		}
		return { ...category, subCategories: tree };
	});

	const remapVideoSubCategories = (video: IVideo) => {
		const mappedIds = _.uniq(
			(video.subCategoriesIds || [])
				.map((id) => globalLegacyToHash.get(id))
				.filter(Boolean) as string[],
		);
		return {
			...video,
			subCategoriesIds: mappedIds,
		};
	};

	const hashedVideos = sortVideosByEpisodeNumber(
		videos.map(remapVideoSubCategories),
	);

	const linksWithCategoriesIds = links.map((link) => {
		const subCategoryPaths = (link.subCategoryPaths || []).map((path) =>
			path.trim(),
		);
		const subCategoryIds = subCategoryPaths
			.map((path) => {
				const key = normalizeFullPathKey(path);
				if (!key) return null;
				return globalPathToHash.get(key) || null;
			})
			.filter(Boolean) as string[];

		return {
			...link,
			categoriesIds: (link.subCategoryPaths || []).map((path) => {
				// Extract category name from path formats like "Category / Subcategory" or "Category/Subcategory"
				const categoryName = path.split("/").map((part) => part.trim()).filter(Boolean)[0];
				return categories.find((category) => category.name === categoryName)?.id;
			}).filter(Boolean),
			subCategoryIds,
		};
	}) as (ILink & { categoriesIds: string[]; subCategoryIds: string[] })[];

	const populateTreeWithVideos = (
		tree: ISubCategory[],
		categoryVideos: IVideo[],
	): ISubCategory[] => {
		return tree
			.map((node) => {
				const nested = populateTreeWithVideos(
					node.subCategories || [],
					categoryVideos,
				);
				const nodeVideos = sortVideosByEpisodeNumber(
					categoryVideos.filter((video) => video.subCategoriesIds.includes(node.id!)),
				);

				if (nested.length === 0 && nodeVideos.length === 0) {
					return null;
				}

				return {
					...node,
					subCategories: nested,
					videos: nodeVideos,
				};
			})
			.filter(Boolean) as ISubCategory[];
	};

	const content = {
		videos: hashedVideos,
		links: linksWithCategoriesIds,
		groups: groups.map((group) => {
			const groupVideos = sortVideosByEpisodeNumber(hashedVideos.filter((video) =>
				isVideoInGroup(video, group),
			));
			const groupLinks = linksWithCategoriesIds.filter((link) => {
				const groupIds = link.groupIds || (link as unknown as { groups?: string[] }).groups || [];
				return groupIds.includes(group.id);
			});
			return {
				...group,
				videos: groupVideos,
				links: groupLinks,
				categories: categoriesWithHashedSubCategories
					.filter((category) => {
						const subCategoriesIds = _.map(category.subCategories, "id");

						for (const video of groupVideos) {
							if (
								_.intersection(video.subCategoriesIds, subCategoriesIds)
									.length > 0
							) {
								return true;
							}
						}

						return false;
					})
					.map((category) => {

						const videos = sortVideosByEpisodeNumber(groupVideos.filter(
							(video) =>
								_.intersection(
									video.subCategoriesIds,
									_.map(category.subCategories, "id"),
								).length > 0,
						));

						const links = groupLinks.filter((link) => {
							if (!link.subCategoryPaths) return false;
							return link.subCategoryPaths.some(path => 
								path.trim().startsWith(`${category.name} /`) || path.trim().startsWith(`${category.name}/`)
							);
						});

						const subCategories = populateTreeWithVideos(
							category.subCategories,
							groupVideos,
						);

						return {
							...category,
							videos: videos,
							links: links,
							subCategories: subCategories,
						};
					})
			}	
		}),
		splash: sortVideosByEpisodeNumber(hashedVideos.filter((video) =>
			video.groups.includes(LOOP_CATEGORIES_NAME),
		)),
	} as const;

	logger.debug("content", content);

	return content;
}
